import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { App as AntApp, Button, Modal } from 'antd';
import useIsDesktop from '../../hooks/useIsDesktop';
import { useAuth } from '../../context/AuthContext';
import { useTour } from '../../learn/TourContext';
import * as memosApi from '../../api/memos';
import { refreshMemoUnread } from '../../hooks/useMemoUnread';
import { refreshUnreadCount } from '../NotifBell';
import { setMemoGateOpen } from '../../lib/memoGateStore';
import MemoBody from './MemoBody';
import { memoDate } from './memoFormat';

/**
 * MemoGate — memos from HQ that pop up, mounted once at the app root (phone and
 * desktop trees), like What's new.
 *
 * Asks GET /memos/pending when the app opens and whenever it comes back to the
 * front (at most once a minute). Each pop-up memo the agent has not confirmed
 * opens over whatever screen they are on — a sheet on the phone, a dialog on
 * desktop — one after another ("1 of 2"). There is no close: only "I have read
 * this", which HQ sees with its time. Every memo put on screen is reported
 * (POST /memos/shown) for HQ's read report.
 *
 * Each check reconciles the queue with the server's list: a memo HQ withdrew, or
 * that no longer reaches this agent, leaves the queue (the one on screen too);
 * the rest keep their order and new ones join the end. If HQ withdraws the memo
 * on screen before the next check, "I have read this" comes back 404 and the
 * memo is simply dropped with a note, so the sheet can never get stuck.
 *
 * Stays out of the public pages (customer signing, guest registration), the
 * sign-in screens, and Learn Mode, whose lessons run on fixtures.
 */
const CHECK_EVERY_MS = 60 * 1000;

const Megaphone = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 11v2a1 1 0 0 0 1 1h3l6 4V6L7 10H4a1 1 0 0 0-1 1z" /><path d="M16.5 8.5a5 5 0 0 1 0 7" /><path d="M19 6a8.5 8.5 0 0 1 0 12" />
  </svg>
);

export default function MemoGate() {
  const { message } = AntApp.useApp();
  const { isAuthed } = useAuth() || {};
  const { active: touring } = useTour() || {};
  const { pathname } = useLocation();
  const desktop = useIsDesktop();
  const [queue, setQueue] = useState([]);
  const [done, setDone] = useState(0);
  const [saving, setSaving] = useState(false);
  const checkedAt = useRef(0);
  const reported = useRef(new Set());
  // Confirmed (or found withdrawn) in this run — a check that was already in flight
  // when the agent confirmed must not bring the memo back.
  const settled = useRef(new Set());

  const suppressed = !isAuthed || touring
    || pathname.startsWith('/sign/') || pathname.startsWith('/join/')
    || pathname === '/login' || pathname === '/reset-password';

  const check = useCallback(async ({ force = false } = {}) => {
    if (!force && Date.now() - checkedAt.current < CHECK_EVERY_MS) return;
    checkedAt.current = Date.now();
    try {
      const r = await memosApi.getPending();
      const popups = (r?.data ?? r)?.popups || [];
      const pending = popups.filter((m) => !settled.current.has(m.id));
      const pendingIds = new Set(pending.map((m) => m.id));
      // Memos no longer pending (withdrawn, out of the audience, read on another
      // device) leave; the rest keep their place; new ones join the end.
      setQueue((q) => {
        const kept = q.filter((m) => pendingIds.has(m.id));
        const keptIds = new Set(kept.map((m) => m.id));
        return [...kept, ...pending.filter((m) => !keptIds.has(m.id))];
      });
    } catch {
      // Tried again the next time the app comes to the front.
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) { setQueue([]); setDone(0); settled.current.clear(); return undefined; }
    // Waits for the screen behind it to paint, as What's new does.
    const t = setTimeout(() => check({ force: true }), 1200);
    const onShow = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('focus', onShow);
    };
  }, [isAuthed, check]);

  // "1 of N" counts from the first memo of a run; an emptied queue starts afresh.
  useEffect(() => { if (!queue.length) setDone(0); }, [queue.length]);

  const current = !suppressed ? queue[0] || null : null;
  useEffect(() => { setMemoGateOpen(!!current); }, [current]);
  useEffect(() => () => setMemoGateOpen(false), []);

  // "Shown" once per memo per launch.
  useEffect(() => {
    if (!current || reported.current.has(current.id)) return;
    reported.current.add(current.id);
    memosApi.markShown([current.id]).catch(() => {});
  }, [current]);

  const acknowledge = async () => {
    if (!current) return;
    setSaving(true);
    const { id } = current;
    try {
      await memosApi.markRead(id);
      settled.current.add(id);
      setDone((d) => d + 1);
      setQueue((q) => q.filter((m) => m.id !== id));
      refreshMemoUnread();
      // Confirming also clears the memo's notification on the bell.
      refreshUnreadCount();
    } catch (e) {
      if (e?.status === 404 || e?.status === 410) {
        // Withdrawn by HQ (or no longer for this agent) while it was on screen.
        settled.current.add(id);
        setQueue((q) => q.filter((m) => m.id !== id));
        message.info('This memo was withdrawn by HQ');
        refreshMemoUnread();
        refreshUnreadCount();
      } else {
        message.error(e?.message || 'Could not record that — please try again');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!current) return null;

  const total = done + queue.length;
  const kicker = `MEMO FROM ${current.from_label.toUpperCase()}${total > 1 ? ` · ${done + 1} OF ${total}` : ''}`;
  const head = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
        <Megaphone />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--primary)' }}>{kicker}</div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, fontWeight: 400 }}>{memoDate(current.publish_at)}</div>
      </div>
    </div>
  );
  const ack = (
    <>
      <Button id="memo-ack" type="primary" block loading={saving} onClick={acknowledge} style={{ height: 48, fontWeight: 600, fontSize: 15 }}>
        I have read this
      </Button>
      <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>
        HQ sees when you confirm · find it again in {desktop ? 'Memos' : 'More → Memos'}
      </div>
    </>
  );

  if (desktop) {
    return (
      <Modal open centered width={600} closable={false} maskClosable={false} keyboard={false} title={head} footer={<div style={{ textAlign: 'left' }}>{ack}</div>}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}>
        <div id="memo-popup">
          <div style={{ fontSize: 18, fontWeight: 650, lineHeight: 1.3, margin: '6px 0 10px' }}>{current.title}</div>
          <MemoBody key={current.id} memo={current} />
        </div>
      </Modal>
    );
  }

  return (
    <div className="sheet-scrim" style={{ position: 'fixed', inset: 0, zIndex: 4650, background: 'rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div id="memo-popup" role="dialog" aria-modal="true" aria-label={current.title} className="sheet-panel"
        style={{ width: '100%', maxWidth: 460, margin: '0 auto', background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '90%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 16px 0', flex: 'none' }}>
          <div style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto' }} />
        </div>
        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 6px' }}>
          {head}
          <div style={{ fontSize: 17, fontWeight: 650, lineHeight: 1.3, margin: '14px 0 10px' }}>{current.title}</div>
          <MemoBody key={current.id} memo={current} />
        </div>
        <div style={{ padding: '14px 16px', paddingBottom: 'calc(14px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
          {ack}
        </div>
      </div>
    </div>
  );
}
